/**
 * Performance API
 * Request tracing and performance monitoring endpoints
 */

import apiClient from './client';

export interface KPICard {
  avg_duration_ms: number | null;
  p95_duration_ms: number | null;
  request_count: number;
  error_rate: number | null;
}

export interface TrendPoint {
  timestamp: string;
  p50_ms: number | null;
  p95_ms: number | null;
  count: number;
}

export interface OverviewResponse {
  kpi: KPICard;
  trend: TrendPoint[];
}

export interface RequestListItem {
  id: number;
  created_at: string;
  user_id: string;
  agent_type: string;
  skill_matched: string | null;
  model_id: string | null;
  is_quick_path: boolean;
  system_hooks_ms: number | null;
  user_hooks_ms: number | null;
  final_hooks_ms: number | null;
  total_rounds: number;
  total_tool_calls: number;
  total_llm_ms: number | null;
  total_tools_ms: number | null;
  first_byte_ms: number | null;
  first_content_ms: number | null;
  total_duration_ms: number | null;
  finish_reason: string | null;
  opik_trace_id: string | null;
}

export interface RequestListResponse {
  items: RequestListItem[];
  total: number;
  page: number;
  size: number;
}

export interface RoundDetail {
  round_index: number;
  ttfb_ms: number | null;
  duration_ms: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cache_read_tokens: number | null;
  cache_creation_tokens: number | null;
  tool_calls_count: number;
}

export interface ToolCallDetail {
  tool_name: string;
  duration_ms: number | null;
  is_error: boolean;
  round_index: number | null;
}

export interface MemoryDecisionDetail {
  decision_index: number;
  memory_id: string | null;
  source: string | null;
  category: string | null;
  score: number | null;
  use_level: string;
  scenario: string | null;
  reason: string | null;
  content_preview: string | null;
  lifecycle: Record<string, unknown>;
  lifecycle_update: Record<string, unknown>;
}

export interface TraceDetailResponse {
  trace: RequestListItem;
  error_message: string | null;
  rounds: RoundDetail[];
  tool_calls: ToolCallDetail[];
  memory_decisions?: MemoryDecisionDetail[];
}

export interface ToolStatsItem {
  tool_name: string;
  call_count: number;
  avg_ms: number | null;
  p95_ms: number | null;
  error_count: number;
}

export const performanceApi = {
  getOverview: async (range = '24h'): Promise<OverviewResponse> => {
    const response = await apiClient.get(`/performance/overview?range=${range}`);
    return response.data;
  },

  listRequests: async (params: {
    range?: string;
    min_duration?: number;
    agent_type?: string;
    skill?: string;
    sort?: string;
    order?: string;
    page?: number;
    size?: number;
  }): Promise<RequestListResponse> => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) qs.set(k, String(v));
    });
    const response = await apiClient.get(`/performance/requests?${qs}`);
    return response.data;
  },

  getRequestDetail: async (traceId: number): Promise<TraceDetailResponse> => {
    const response = await apiClient.get(`/performance/requests/${traceId}`);
    return response.data;
  },

  getToolStats: async (range = '24h'): Promise<ToolStatsItem[]> => {
    const response = await apiClient.get(`/performance/tools/stats?range=${range}`);
    return response.data;
  },
};
